import React, { memo } from 'react';
import { validators } from 'investira.sdk';
import { Chip, Icon, Tooltip, Stack, IconButton, Typography } from 'investira.react.components';
import { ASSINATURA_ESTAGIO, ASSINATURA_TIPO } from '../../../enums';

/**
 * Renderiza o componente AssinaturaEstagio.
 *
 * @param {Object} props - Propriedades do componente.
 * @param {number} props.value - Valor exibido ou controlado pelo componente.
 * @param {string} props.variant - Variante visual ou de comportamento do componente.
 * @param {string} props.registradorUsername - Propriedade `registradorUsername` do componente.
 * @param {string} props.conferidorUsername - Propriedade `conferidorUsername` do componente.
 * @param {string} props.verificadorUsername - Propriedade `verificadorUsername` do componente.
 * @param {string} props.aprovadorUsername - Propriedade `aprovadorUsername` do componente.
 * @returns {JSX.Element|null} Elemento renderizado ou `null` quando não houver conteúdo para exibir.
 */
const AssinaturaEstagio = memo(props => {
  const {
    value = ASSINATURA_ESTAGIO.RG.id,
    variant = 'default',
    registradorUsername = null,
    conferidorUsername = null,
    verificadorUsername = null,
    aprovadorUsername = null
  } = props;

  if (validators.isEmpty(value)) {
    return null;
  }

  const getEstagioByValue = pValor => {
    const xEstagio = Object.values(ASSINATURA_ESTAGIO).find(xTipo => xTipo.id === pValor);
    if (validators.isEmpty(xEstagio)) {
      return ASSINATURA_ESTAGIO.RG;
    }
    return xEstagio;
  };

  const { id, descricao, icon, iconColor, color, iconSize } = getEstagioByValue(value);

  const isAprovador = id === ASSINATURA_ESTAGIO.AP.id;
  const isVerificador = id === ASSINATURA_TIPO.VR.id;
  const isConferidor = id === ASSINATURA_TIPO.CN.id;

  if (variant === 'icon') {
    return (
      <Tooltip
        arrow
        placement="top"
        componentsProps={{
          tooltip: {
            sx: {
              maxWidth: 'min(90vw, 520px)'
            }
          }
        }}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 'min(90vw, 520px)'
            }
          }
        }}
        title={
          <Stack
            sx={{
              px: 0.5,
              py: 0.5,
              width: 'fit-content',
              maxWidth: '100%'
            }}>
            <Typography variant="caption" color="textPrimary">
              Estágio: <b>{descricao}</b>.
            </Typography>
            {aprovadorUsername && (
              <Typography
                variant="caption"
                color={isAprovador ? 'textPrimary' : 'textSecondary'}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}>
                Aprovado por:{' '}
                <span style={{ fontWeight: isAprovador ? 700 : 400 }}>{aprovadorUsername}</span>
              </Typography>
            )}
            {verificadorUsername && (
              <Typography
                variant="caption"
                color={isVerificador ? 'textPrimary' : 'textSecondary'}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}>
                Verificado por:{' '}
                <span style={{ fontWeight: isVerificador ? 700 : 400 }}>{verificadorUsername}</span>
              </Typography>
            )}
            {conferidorUsername && (
              <Typography
                variant="caption"
                color={isConferidor ? 'textPrimary' : 'textSecondary'}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}>
                Conferido por:{' '}
                <span style={{ fontWeight: isConferidor ? 700 : 400 }}>{conferidorUsername}</span>
              </Typography>
            )}
            {registradorUsername && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word'
                }}>
                Registrado por: {registradorUsername}
              </Typography>
            )}
          </Stack>
        }>
        <IconButton color={iconColor}>
          <Icon iconName={icon} size={iconSize} color={iconColor} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Chip
      icon={<Icon iconName={icon} size={iconSize} />}
      label={descricao}
      size="small"
      color={color}
      sx={{ px: 1, py: 0.5, whiteSpace: 'normal' }}
    />
  );
});

export default AssinaturaEstagio;
